/**
 * "Add this to your home screen" — who to say it to, and what to tell them.
 *
 * The league opens this app on a phone, on draft night, in a room with bad wifi. Run from the home
 * screen it is a standalone window with the service worker's cached shell behind it; run from a
 * browser tab it is a tab somebody's phone will happily evict mid-auction. So the offer is worth
 * making — once, politely, with a way to say never.
 *
 * ALL OF IT IS PURE. Detection takes a user-agent string rather than reading `navigator`, and the
 * instructions come back as data rather than as markup, because the interesting cases are the ones
 * no test machine has: an iPad that calls itself a Mac, Chrome on iOS, Samsung Internet. Those are
 * checked here against real UA strings instead of being discovered on somebody's phone.
 *
 * The browser facts behind the copy, checked against MDN in August 2026:
 *
 *   - `beforeinstallprompt` is Android and desktop only. NO iOS browser fires it, so on iOS there
 *     is never a button to press and the instructions are the whole feature.
 *   - iOS 16.4 (March 2023) opened installing to Chrome, Edge and Firefox via the system Share
 *     sheet. Before that it was Safari alone. Three years on, telling an iPhone user to switch to
 *     Safari would be wrong more often than right — so each iOS browser gets its own share path.
 *   - On Android, Chrome, Edge, Firefox, Opera and Samsung Internet can all install.
 */

/** The three that matter. `other` is a desktop, or something we do not recognise — no offer. */
export type Family = 'ios' | 'android' | 'other';
export type Browser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'opera' | 'samsung' | 'other';

export interface Device {
  family: Family;
  browser: Browser;
}

export interface Guide {
  /** Names the device in the words its owner would use. */
  title: string;
  /** In order. Each is a whole instruction, not a fragment. */
  steps: string[];
  /** True where this browser MAY fire `beforeinstallprompt` — never on iOS. */
  mayPrompt: boolean;
  /** Shown under the steps where there is something else worth knowing. */
  note?: string;
}

/**
 * What device is this, from a UA string.
 *
 * `touchPoints` is not optional decoration: since iPadOS 13 an iPad reports itself as `Macintosh`
 * and is indistinguishable from a laptop by user-agent alone. `navigator.maxTouchPoints > 1` is
 * the standard way back — a Mac reports 0, an iPad reports 5. Get this wrong and every iPad in the
 * league is told it is a desktop and never sees the offer.
 */
export function readDevice(ua: string, touchPoints = 0): Device {
  const s = ua || '';
  const iPadAsMac = /Macintosh/i.test(s) && touchPoints > 1;
  const family: Family = /iPhone|iPad|iPod/i.test(s) || iPadAsMac
    ? 'ios'
    : /Android/i.test(s) ? 'android' : 'other';

  if (family === 'ios') {
    /*
     * Every iOS browser is WebKit underneath and every one of them says `Safari` somewhere in its
     * UA, so Safari is what is left after the others are named — never what is matched for.
     */
    if (/CriOS/i.test(s)) return { family, browser: 'chrome' };
    if (/EdgiOS/i.test(s)) return { family, browser: 'edge' };
    if (/FxiOS/i.test(s)) return { family, browser: 'firefox' };
    if (/OPiOS|OPT\//i.test(s)) return { family, browser: 'opera' };
    return { family, browser: 'safari' };
  }

  if (family === 'android') {
    // Order matters: Samsung Internet, Edge and Opera all carry `Chrome` in their UA as well.
    if (/SamsungBrowser/i.test(s)) return { family, browser: 'samsung' };
    if (/EdgA/i.test(s)) return { family, browser: 'edge' };
    if (/OPR\//i.test(s)) return { family, browser: 'opera' };
    if (/Firefox/i.test(s)) return { family, browser: 'firefox' };
    if (/Chrome/i.test(s)) return { family, browser: 'chrome' };
    return { family, browser: 'other' };
  }

  return { family: 'other', browser: 'other' };
}

const IOS_LAST = 'Give it a name and tap Add.';

export function guideFor(d: Device): Guide {
  if (d.family === 'ios') {
    /*
     * The share control is in a different place in each of these, and "tap Share" on its own is
     * the instruction people get stuck on — so each step says WHERE.
     */
    if (d.browser === 'safari') {
      return {
        title: 'On iPhone and iPad, in Safari',
        steps: [
          'Tap the Share button — the square with an arrow, in the bar at the bottom.',
          'Scroll down the list and tap Add to Home Screen.',
          IOS_LAST,
        ],
        mayPrompt: false,
      };
    }
    if (d.browser === 'firefox') {
      return {
        title: 'On iPhone and iPad, in Firefox',
        steps: [
          'Tap the menu button at the bottom right, then Share.',
          'Scroll down the list and tap Add to Home Screen.',
          IOS_LAST,
        ],
        mayPrompt: false,
        note: 'Needs iOS 16.4 or newer. On an older iPhone, open the page in Safari instead.',
      };
    }
    const named = d.browser === 'chrome' ? 'Chrome' : d.browser === 'edge' ? 'Edge' : 'Opera';
    return {
      title: `On iPhone and iPad, in ${named}`,
      steps: [
        'Tap the Share button in the address bar at the top.',
        'Scroll down the list and tap Add to Home Screen.',
        IOS_LAST,
      ],
      mayPrompt: false,
      note: 'Needs iOS 16.4 or newer. On an older iPhone, open the page in Safari instead.',
    };
  }

  if (d.family === 'android') {
    if (d.browser === 'samsung') {
      return {
        title: 'On Android, in Samsung Internet',
        steps: [
          'Tap the menu button at the bottom right.',
          'Tap Add page to, then Home screen.',
          'Tap Add.',
        ],
        mayPrompt: false,
      };
    }
    if (d.browser === 'firefox') {
      return {
        title: 'On Android, in Firefox',
        steps: [
          'Tap the menu button — the three dots at the top right.',
          'Tap Add to Home screen.',
          'Tap Add.',
        ],
        mayPrompt: false,
      };
    }
    const named = d.browser === 'edge' ? 'Edge' : d.browser === 'opera' ? 'Opera' : 'Chrome';
    return {
      title: `On Android, in ${named}`,
      steps: [
        'Tap the menu button — the three dots at the top right.',
        'Tap Install app, or Add to Home screen.',
        'Tap Install.',
      ],
      /*
       * Only Chromium-family Android browsers are claimed here. Firefox does not implement
       * `beforeinstallprompt` at all, and Samsung Internet's has been reported firing and not
       * firing across its own point releases — neither is something to write copy against. The
       * button in the UI appears only if the event ACTUALLY fires, so this flag decides wording,
       * never capability.
       */
      mayPrompt: true,
    };
  }

  return {
    title: 'On a computer',
    steps: ['Use the install icon in the address bar, if your browser offers one.'],
    mayPrompt: true,
  };
}

export interface OfferInput {
  device: Device;
  /** Already running from the home screen — the one case where the offer is simply wrong. */
  standalone: boolean;
  /** "Do not show again", from storage. */
  dismissedForever: boolean;
  /** Closed earlier in this browsing session, including before an OAuth round trip. */
  closedThisSession: boolean;
}

/**
 * Four ways to be told no, and the offer respects all of them.
 *
 * Desktop is excluded because the ask makes no sense there — the app is already a tab beside the
 * other tabs, and nothing about draft night improves by pinning it.
 */
export function shouldOffer(o: OfferInput): boolean {
  if (o.device.family === 'other') return false;
  if (o.standalone) return false;
  if (o.dismissedForever) return false;
  if (o.closedThisSession) return false;
  return true;
}

/**
 * Is this already the installed app?
 *
 * Two checks because the two platforms answer differently: `display-mode: standalone` is the
 * standard and works on Android, while iOS carries the non-standard `navigator.standalone`. An
 * iPhone running the installed app reports `false` for the media query in some versions, so
 * missing the second check shows the "add me to your home screen" card INSIDE the home-screen app.
 */
export function isStandalone(w: Window | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  if (!w) return false;
  const legacy = (w.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const media = typeof w.matchMedia === 'function'
    && w.matchMedia('(display-mode: standalone)').matches;
  return legacy || media;
}

/** One key, in one place, so the checkbox and the reader cannot drift. */
export const NEVER_KEY = 'ffl.addToHome.never';
export const SESSION_KEY = 'ffl.addToHome.closed';
